if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopRailMql = window.matchMedia('(min-width: 990px)');
        this.finePointerMql = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.handleThumbnailPreview = this.handleThumbnailPreview.bind(this);
        this.handleSwatchPreview = this.handleSwatchPreview.bind(this);
        this.productInfo = this.closest('product-info');
        if (!this.elements.thumbnails) return;

        this.thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        this.thumbnailRailButtons = this.elements.thumbnails.querySelectorAll('.slider-button');
        this.handleThumbnailRailNavigation = this.handleThumbnailRailNavigation.bind(this);
        this.updateThumbnailRailButtons = this.updateThumbnailRailButtons.bind(this);

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.thumbnailPreviewTargets = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        this.thumbnailPreviewTargets.forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          mediaToSwitch.addEventListener('pointerenter', this.handleThumbnailPreview);
          mediaToSwitch.addEventListener('focusin', this.handleThumbnailPreview);
        });
        this.productInfo?.addEventListener('pointerover', this.handleSwatchPreview);
        this.productInfo?.addEventListener('focusin', this.handleSwatchPreview);
        this.thumbnailRailButtons.forEach((button) =>
          button.addEventListener('click', this.handleThumbnailRailNavigation)
        );
        this.thumbnailList?.addEventListener('scroll', this.updateThumbnailRailButtons, { passive: true });
        this.thumbnailRailResizeObserver = new ResizeObserver(this.updateThumbnailRailButtons);
        if (this.thumbnailList) this.thumbnailRailResizeObserver.observe(this.thumbnailList);
        requestAnimationFrame(this.updateThumbnailRailButtons);
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.thumbnailPreviewTargets?.forEach((previewTarget) => {
          previewTarget.removeEventListener('pointerenter', this.handleThumbnailPreview);
          previewTarget.removeEventListener('focusin', this.handleThumbnailPreview);
        });
        this.productInfo?.removeEventListener('pointerover', this.handleSwatchPreview);
        this.productInfo?.removeEventListener('focusin', this.handleSwatchPreview);
        this.thumbnailRailButtons?.forEach((button) =>
          button.removeEventListener('click', this.handleThumbnailRailNavigation)
        );
        this.thumbnailList?.removeEventListener('scroll', this.updateThumbnailRailButtons);
        this.thumbnailRailResizeObserver?.disconnect();
      }

      handleThumbnailPreview(event) {
        if (!this.finePointerMql.matches) return;
        const previewTarget = event.currentTarget;
        this.setActiveMedia(previewTarget.dataset.target, false);
      }

      handleSwatchPreview(event) {
        if (!this.finePointerMql.matches) return;
        const swatch = event.target.closest('[data-islander-preview-media-id]');
        const globoSwatch = event.target.closest('.globo-swatch-product-detail .select-option[data-value]');
        if ((!swatch && !globoSwatch) || !this.productInfo?.contains(swatch || globoSwatch)) return;
        const mediaId =
          swatch?.dataset.islanderPreviewMediaId ||
          this.getPreviewMediaIdForOptionValue(globoSwatch?.dataset.value);
        if (!mediaId) return;
        this.setActiveMedia(`${this.dataset.section}-${mediaId}`, false);
      }

      getPreviewMediaIdForOptionValue(optionValue) {
        if (!optionValue) return null;
        const nativeSwatch = Array.from(
          this.productInfo?.querySelectorAll('[data-islander-preview-media-id]') || []
        ).find((nativeSwatch) => nativeSwatch.control?.value === optionValue);
        return nativeSwatch?.dataset.islanderPreviewMediaId || null;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isVerticalThumbnailRail()) {
          const listRect = this.thumbnailList.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          const isVisible = thumbnailRect.top >= listRect.top + 4 && thumbnailRect.bottom <= listRect.bottom - 4;

          if (!isVisible) {
            const centredTop = thumbnail.offsetTop - (this.thumbnailList.clientHeight - thumbnail.offsetHeight) / 2;
            this.thumbnailList.scrollTo({ top: centredTop, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      isVerticalThumbnailRail() {
        return (
          this.desktopRailMql.matches &&
          this.elements.thumbnails.hasAttribute('data-islander-thumbnail-rail') &&
          this.thumbnailList
        );
      }

      handleThumbnailRailNavigation(event) {
        if (!this.isVerticalThumbnailRail()) return;

        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        const distance = Math.max(this.thumbnailList.clientHeight * 0.75, 96);
        this.thumbnailList.scrollBy({ top: direction * distance, behavior: 'smooth' });
      }

      updateThumbnailRailButtons() {
        if (!this.isVerticalThumbnailRail()) return;

        const maximumScroll = this.thumbnailList.scrollHeight - this.thumbnailList.clientHeight;
        this.thumbnailRailButtons.forEach((button) => {
          const atStart = this.thumbnailList.scrollTop <= 1;
          const atEnd = this.thumbnailList.scrollTop >= maximumScroll - 1;
          button.disabled = maximumScroll <= 1 || (button.name === 'previous' ? atStart : atEnd);
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
